import { useEffect, useState } from "react";
import { http } from "../lib/api";
import { X, Send, Loader2, Check, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";

export const InquiryModal = ({ tier, open, onClose }) => {
  const [form, setForm] = useState({
    name: "", email: "", business: "", description: "",
    budget_confirmed: false, contact_pref: "email",
  });
  const [busy, setBusy] = useState(false);
  const [payingBusy, setPayingBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [stripeUrl, setStripeUrl] = useState(null); // set after checkout session is created

  useEffect(() => {
    if (!open) return;
    setSent(false);
    setStripeUrl(null);
    setForm({ name: "", email: "", business: "", description: "", budget_confirmed: false, contact_pref: "email" });
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open || !tier) return null;

  const validateBase = () => {
    if (!form.name.trim() || !form.email.trim() || !form.description.trim()) {
      toast.error("please fill name, email, and description");
      return false;
    }
    // simple but effective email format check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      toast.error("please enter a valid email (like you@example.com)");
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateBase()) return;
    if (!form.budget_confirmed) {
      toast.error("please confirm you understand the price");
      return;
    }
    setBusy(true);
    try {
      const res = await http.post("/business/inquire", { ...form, tier_id: tier.id });
      if (res.data.ok) {
        setSent(true);
        toast.success("inquiry sent");
        track("inquiry_submitted", {
          tier_id: tier.id, tier_name: tier.name, price_usd: tier.price_usd,
          value: tier.price_usd, currency: "USD",
        });
      } else {
        toast.error("something went wrong");
        track("inquiry_submit_failed", { tier_id: tier.id, reason: "api_returned_not_ok" });
      }
    } catch (err) {
      toast.error("submit failed: " + (err.response?.data?.detail || err.message));
      track("inquiry_submit_failed", { tier_id: tier.id, reason: err.message });
    }
    setBusy(false);
  };

  const payNow = async () => {
    if (!validateBase()) return;
    if (!form.budget_confirmed) {
      toast.error("please confirm you understand the price");
      return;
    }
    setPayingBusy(true);
    setStripeUrl(null);
    track("business_checkout_started", {
      tier_id: tier.id, tier_name: tier.name, price_usd: tier.price_usd,
      value: tier.price_usd, currency: "USD",
    });
    try {
      const res = await http.post("/business/checkout", {
        tier_id: tier.id,
        name: form.name,
        email: form.email,
        business: form.business,
        description: form.description,
        contact_pref: form.contact_pref,
        success_url: window.location.origin + "/business/success",
        cancel_url: window.location.origin + "/business",
      });
      if (res.data.session_url) {
        setStripeUrl(res.data.session_url);
      } else {
        toast.error("couldn't start checkout");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`checkout failed: ${detail}`);
      track("business_checkout_failed", { tier_id: tier.id, reason: detail });
    }
    setPayingBusy(false);
  };

  const priceLabel = tier.cadence === "monthly" ? `$${tier.price_usd.toLocaleString()}/mo` : `$${tier.price_usd.toLocaleString()}`;
  const isRecurring = tier.cadence === "monthly";
  const anyBusy = busy || payingBusy;

  return (
    <div
      data-testid="inquiry-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl bg-[#0a0a0a] border border-white/15 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="inquiry-close"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 text-neutral-500 hover:text-white z-10"
          style={{ transitionProperty: "color", transitionDuration: "150ms" }}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-8 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-2">
            // inquire // {tier.name}
          </div>
          <h2 className="font-display font-bold text-3xl tracking-tight">{priceLabel}</h2>
          <p className="text-neutral-400 text-sm mt-2">{tier.tagline}</p>
        </div>

        {sent ? (
          <div data-testid="inquiry-success" className="p-12 text-center">
            <div className="w-14 h-14 mx-auto mb-4 border border-[color:var(--accent-matrix)] flex items-center justify-center">
              <Check className="w-6 h-6 text-[color:var(--accent-matrix)]" />
            </div>
            <div className="font-display font-bold text-2xl">inquiry received</div>
            <p className="text-neutral-400 mt-3 text-sm">
              You&apos;ll hear back within 48 hours to schedule a 15-min free fit call. No spam, no upsells.
            </p>
            <button
              data-testid="inquiry-done"
              onClick={onClose}
              className="mt-6 border border-white/20 px-5 py-2.5 text-[10px] uppercase tracking-widest hover:border-white/60 duration-150"
              style={{ transitionProperty: "border-color" }}
            >
              close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 space-y-4">
            <Field label="your name" testId="inquiry-name">
              <input
                data-testid="inquiry-input-name"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none px-3 py-2.5 text-sm"
                style={{ transitionProperty: "border-color", transitionDuration: "150ms" }}
                required
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="email">
                <input
                  data-testid="inquiry-input-email"
                  type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none px-3 py-2.5 text-sm"
                  style={{ transitionProperty: "border-color", transitionDuration: "150ms" }}
                  required
                />
              </Field>
              <Field label="business / brand name (optional)">
                <input
                  data-testid="inquiry-input-business"
                  value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })}
                  className="w-full bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none px-3 py-2.5 text-sm"
                  style={{ transitionProperty: "border-color", transitionDuration: "150ms" }}
                />
              </Field>
            </div>
            <Field label="describe your situation & what you want out of this">
              <textarea
                data-testid="inquiry-input-description"
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={5}
                placeholder="what's the current state? what would 'done' look like? any deadlines?"
                className="w-full bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none p-3 text-sm resize-none"
                style={{ transitionProperty: "border-color", transitionDuration: "150ms" }}
                required
              />
            </Field>

            <label className="flex items-start gap-2 cursor-pointer" data-testid="inquiry-budget-toggle">
              <input
                type="checkbox"
                checked={form.budget_confirmed}
                onChange={(e) => setForm({ ...form, budget_confirmed: e.target.checked })}
                className="mt-1 accent-[color:var(--accent-amber)]"
              />
              <span className="text-xs text-neutral-300 leading-relaxed">
                I understand this engagement is <span className="text-[color:var(--accent-amber)] font-bold">{priceLabel}</span> and I&apos;m ready to invest at this level.
              </span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="submit"
                data-testid="inquiry-submit"
                disabled={anyBusy}
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-4 py-3 text-xs uppercase tracking-widest font-bold hover:border-white/60 hover:bg-white/5 disabled:opacity-50 duration-150"
                style={{ transitionProperty: "background-color, border-color, opacity" }}
                title="submit an inquiry — we'll schedule a free 15-min fit call first"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {busy ? "sending..." : "inquiry (free call)"}
              </button>
              <button
                type="button"
                data-testid="inquiry-pay-now"
                onClick={payNow}
                disabled={anyBusy || !!stripeUrl}
                className="inline-flex items-center justify-center gap-2 bg-[color:var(--accent-amber)] text-black px-4 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50 duration-150"
                style={{ transitionProperty: "background-color, opacity" }}
                title="pay now and skip the fit call"
              >
                {payingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                {payingBusy ? "preparing stripe..." : (isRecurring ? `subscribe · ${priceLabel}` : `pay now · ${priceLabel}`)}
              </button>
            </div>

            {/* Once Stripe session is ready, show a bulletproof top-frame link.
                target="_top" is the only reliable way to escape the Emergent preview iframe. */}
            {stripeUrl && (
              <div
                data-testid="stripe-continue-panel"
                className="mt-4 p-4 border border-[color:var(--accent-matrix)] bg-[color:var(--accent-matrix)]/5"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-2">// stripe checkout ready</div>
                <p className="text-xs text-neutral-300 mb-3 leading-relaxed">
                  tap below to open the secure Stripe payment page. it opens in the same tab and
                  will redirect you back here after payment.
                </p>
                <a
                  href={stripeUrl}
                  target="_top"
                  rel="noopener"
                  data-testid="stripe-continue-link"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--accent-matrix)] text-black px-4 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white duration-150"
                  style={{ transitionProperty: "background-color" }}
                >
                  <CreditCard className="w-4 h-4" />
                  continue to stripe · {priceLabel}
                </a>
                <div className="mt-2 text-[10px] text-neutral-500 text-center">
                  test cards: 4242 4242 4242 4242 · any future date · any 3-digit CVC
                </div>
              </div>
            )}

            <p className="text-[10px] text-neutral-500 text-center leading-relaxed">
              <span className="text-neutral-400">Inquiry</span> → free 15-min fit call → invoice.
              <span className="mx-2">·</span>
              <span className="text-neutral-400">Pay now</span> → work starts immediately, refund per tier guarantee.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
